import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Archive, ArchiveRestore, Trash2, Car, X, Zap, Fuel, Plus, ArrowUpRight, ArrowDownRight, ChevronRight, ChevronLeft, Info as InfoIcon, ClipboardList, Gauge, Shield, ChartLine, Car as CarIcon, Wrench, Calendar as CalendarIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { categoryIcon, statusMeta, formatRemaining, sortByUrgency } from "@/lib/maintenanceMeta";
import { TESLA_COLORS, findColorByName } from "@/lib/teslaColors";
import { fromKm, toKm, fmt, unitLabel } from "@/lib/units";
import { fmtMoney } from "@/lib/currency";
import { Bar, BarChart, ResponsiveContainer } from "recharts";
import { EnergyAPI, MaintenanceAPI, resolveImageUrl } from "@/lib/api";
import MaintenanceStatusCard from "@/components/MaintenanceStatusCard";
import logger from "@/lib/logger";
import VehiclePhotoPicker from "@/components/VehiclePhotoPicker";
import { useThemeColors } from "@/lib/theme";
import useVehicleOdometer, { odometerSourceLabel } from "@/lib/useVehicleOdometer";
import useConfirm from "@/lib/useConfirm";

const WHEEL_SIZES = ['15"','16"','17"','18"', '19"', '20"', '21"','22"',"Other"];

function AvgEfficiencyBlock({ eff, energyType, unit }) {
  if (!eff) return null;
  const evAvg = eff.avg_km_per_kwh;
  const whAvg = eff.avg_wh_per_km;
  const fuelAvg = eff.avg_l_per_100km;
  const trend = eff.trend || {};

  // Decide which metric to show. Hybrid: prefer EV when both exist, otherwise fuel.
  const wantsEv =
    (energyType === "ev" || energyType === "hybrid") && evAvg != null;
  const wantsFuel = !wantsEv && fuelAvg != null;
  if (!wantsEv && !wantsFuel) return null;

  // 30-day trend delta
  let delta = null;
  let goodWhenUp = true;
  if (wantsEv && trend.last30_km_per_kwh != null && trend.prev30_km_per_kwh != null && trend.prev30_km_per_kwh > 0) {
    delta = ((trend.last30_km_per_kwh - trend.prev30_km_per_kwh) / trend.prev30_km_per_kwh) * 100;
    goodWhenUp = true;
  } else if (wantsFuel && trend.last30_l_per_100km != null && trend.prev30_l_per_100km != null && trend.prev30_l_per_100km > 0) {
    delta = ((trend.last30_l_per_100km - trend.prev30_l_per_100km) / trend.prev30_l_per_100km) * 100;
    goodWhenUp = false;
  }

  let primary = null;
  let secondary = null;
  let icon = null;
  if (wantsEv) {
    icon = <Zap className="w-3.5 h-3.5" strokeWidth={1.75} />;
    if (unit === "mi") {
      primary = `${(evAvg / 1.60934).toFixed(1)} mi/kWh`;
      secondary = whAvg != null ? `${(whAvg * 1.60934).toFixed(0)} Wh/mi` : null;
    } else {
      primary = `${evAvg.toFixed(1)} km/kWh`;
      secondary = whAvg != null ? `${whAvg.toFixed(0)} Wh/km` : null;
    }
  } else if (wantsFuel) {
    icon = <Fuel className="w-3.5 h-3.5" strokeWidth={1.75} />;
    if (unit === "mi") {
      const mpg = fuelAvg > 0 ? 235.215 / fuelAvg : null;
      primary = mpg != null ? `${mpg.toFixed(1)} mpg` : null;
      secondary = `${fuelAvg.toFixed(1)} L/100km`;
    } else {
      primary = `${fuelAvg.toFixed(1)} L/100km`;
      secondary = "lower is better";
    }
  }

  let trendNode = null;
  if (delta != null && isFinite(delta) && Math.abs(delta) >= 0.5) {
    const up = delta > 0;
    const good = up === goodWhenUp;
    const arrowColor = good ? "text-[#34D17A]" : "text-[#FF5A5F]";
    const Arrow = up ? ArrowUpRight : ArrowDownRight;
    trendNode = (
      <span
        className="inline-flex items-center gap-0.5 text-[10px] tracking-wider font-mono-data text-[var(--text-muted)]"
        data-testid="detail-avg-eff-trend"
      >
        <Arrow className={`w-3 h-3 ${arrowColor}`} strokeWidth={2} />
        <span className="text-[var(--text-primary)]">{up ? "+" : ""}{delta.toFixed(0)}%</span>
        <span>vs last 30d</span>
      </span>
    );
  }

  return (
    <div
      className="bg-[var(--bg-canvas)] border border-[var(--border-hover)] p-5 flex items-center justify-between"
      style={{ borderRadius: 2 }}
      data-testid="detail-avg-efficiency"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 flex items-center justify-center bg-[var(--bg-emphasis)] border border-[var(--border-hover)] text-[var(--text-primary)]"
          style={{ borderRadius: 999 }}
        >
          {icon}
        </div>
        <div>
          <div className="label-tiny mb-0.5">Average Efficiency</div>
          <div className="font-mono-data text-lg text-[var(--text-primary)] leading-none">{primary}</div>
          {secondary && (
            <div className="text-[11px] text-[var(--text-muted)] font-mono-data mt-1">{secondary}</div>
          )}
        </div>
      </div>
      {trendNode}
    </div>
  );
}

export default function VehicleDetailSheet({
  open,
  onOpenChange,
  vehicle,
  unit,
  stats,
  onUpdate,
  onDelete,
  onArchive,
  currencySymbol = "$",
  onAddEnergy,
}) {
  const [form, setForm] = useState(null);
  const [energyEvents, setEnergyEvents] = useState([]);
  const [openSection, setOpenSection] = useState(null); // accordion: only one open at a time
  const [view, setView] = useState("main"); // "main" | "energy-history"
  const tc = useThemeColors();
  // In-app confirm — `window.confirm` is blocked in the Emergent preview iframe.
  const { confirm, ConfirmDialog } = useConfirm();

  // Authoritative current odometer — resolved via the shared service so
  // this sheet reflects the same reading as VehicleCard and any future
  // OEM telemetry provider. Never derive locally.
  const sheetOdo = useVehicleOdometer(vehicle?.id);

  useEffect(() => {
    if (vehicle) {
      setForm({
        name: vehicle.name || "",
        make: vehicle.make || "",
        model: vehicle.model || "",
        year: vehicle.year || "",
        vin: vehicle.vin || "",
        wheel_size: vehicle.wheel_size || "",
        paint_name: vehicle.paint_name || "",
        image_url: vehicle.image_url || "",
        energy_type: vehicle.energy_type || "ev",
        usable_battery_kwh:
          vehicle.usable_battery_kwh != null ? String(vehicle.usable_battery_kwh) : "",
        fuel_tank_capacity_l:
          vehicle.fuel_tank_capacity_l != null ? String(vehicle.fuel_tank_capacity_l) : "",
        yearly_limit_display: fromKm(vehicle.yearly_limit_km || 0, unit).toFixed(0),
        insurance_company: vehicle.insurance_company || "",
        policy_number: vehicle.policy_number || "",
        policy_start_date: vehicle.policy_start_date || "",
        // ----- Refactor (Feb 2026) — new persisted fields -----
        current_odometer_km:
          vehicle.current_odometer_km != null
            ? String(fromKm(vehicle.current_odometer_km, unit).toFixed(0))
            : "",
        purchase_date: vehicle.purchase_date || "",
        primary_usage_type: vehicle.primary_usage_type || "personal",
        epa_kwh_per_100km:
          vehicle.epa_kwh_per_100km != null ? String(vehicle.epa_kwh_per_100km) : "",
        epa_l_per_100km:
          vehicle.epa_l_per_100km != null ? String(vehicle.epa_l_per_100km) : "",
      });
    }
  }, [vehicle, unit]);

  // Reset accordion + sub-view whenever the sheet closes — avoids confusing
  // open-state leaks across vehicles.
  useEffect(() => {
    if (!open) {
      setOpenSection(null);
      setView("main");
    }
  }, [open]);

  // Fetch energy events for this vehicle when open
  useEffect(() => {
    if (open && vehicle?.id) {
      EnergyAPI.list({ vehicle_id: vehicle.id })
        .then(setEnergyEvents)
        .catch(() => setEnergyEvents([]));
    }
  }, [open, vehicle?.id]);

  // Fetch maintenance summary + full item list for this vehicle. The summary
  // drives the "Next Due" row; the sorted items drive the 3 "At a glance" pills.
  const [maintSummary, setMaintSummary] = useState(null);
  const [maintItems, setMaintItems] = useState([]);
  useEffect(() => {
    if (open && vehicle?.id) {
      MaintenanceAPI.summary({ vehicle_id: vehicle.id })
        .then(setMaintSummary)
        .catch(() => setMaintSummary(null));
      MaintenanceAPI.list({ vehicle_id: vehicle.id })
        .then((items) => setMaintItems(sortByUrgency(items)))
        .catch(() => setMaintItems([]));
    }
  }, [open, vehicle?.id]);

  const navigate = useNavigate();

  if (!vehicle || !form) return null;

  const paint = findColorByName(form.paint_name);
  const usedKm = stats?.year_used_km || 0;
  const limit = vehicle.yearly_limit_km || 0;
  const pct = limit > 0 ? Math.min(100, (usedKm / limit) * 100) : 0;
  const overLimit = usedKm > limit;

  const monthly = (stats?.monthly || []).map((m) => ({
    month: m.month.slice(5),
    value: fromKm(m.distance_km, unit),
  }));

  const save = async () => {
    await onUpdate(vehicle.id, {
      name: form.name,
      make: form.make,
      model: form.model,
      year: form.year ? parseInt(form.year) : null,
      vin: form.vin,
      wheel_size: form.wheel_size,
      paint_name: form.paint_name,
      color: paint?.hex || vehicle.color,
      image_url: form.image_url,
      energy_type: form.energy_type,
      usable_battery_kwh:
        form.usable_battery_kwh !== "" ? parseFloat(form.usable_battery_kwh) : null,
      fuel_tank_capacity_l:
        form.fuel_tank_capacity_l !== "" ? parseFloat(form.fuel_tank_capacity_l) : null,
      yearly_limit_km: toKm(parseFloat(form.yearly_limit_display) || 0, unit),
      insurance_company: form.insurance_company,
      policy_number: form.policy_number,
      policy_start_date: form.policy_start_date,
      // New persisted fields (iteration: Vehicle Detail refactor Feb 2026)
      current_odometer_km:
        form.current_odometer_km !== ""
          ? toKm(parseFloat(form.current_odometer_km) || 0, unit)
          : null,
      purchase_date: form.purchase_date || "",
      primary_usage_type: form.primary_usage_type || "personal",
      epa_kwh_per_100km:
        form.epa_kwh_per_100km !== "" ? parseFloat(form.epa_kwh_per_100km) : null,
      epa_l_per_100km:
        form.epa_l_per_100km !== "" ? parseFloat(form.epa_l_per_100km) : null,
    });
    onOpenChange(false);
  };

  const handleDeleteEnergy = async (id) => {
    const ok = await confirm({
      title: "Delete energy entry?",
      description:
        "This permanently removes the charging session or fuel fill-up. This can't be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await EnergyAPI.remove(id);
      setEnergyEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      logger.error("Failed to delete energy event", err, { eventId: id });
    }
  };

  const totalSpend = energyEvents.reduce((acc, e) => acc + (e.cost || 0), 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[640px] bg-[var(--bg-deep)] border-l border-[var(--border-hover)] text-[var(--text-primary)] p-0 overflow-y-auto pb-24 [&>button]:hidden"
        data-testid="vehicle-detail-sheet"
        aria-describedby={undefined}
      >
        <SheetTitle className="sr-only">Vehicle Details</SheetTitle>
        {/* Custom close button — wrapped in div so [&>button]:hidden only targets default shadcn close */}
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={() => onOpenChange(false)}
            className="w-9 h-9 bg-[var(--bg-canvas)]/90 backdrop-blur-md border border-[var(--border-hover)] hover:border-[#E82127] hover:text-[#E82127] flex items-center justify-center transition-colors"
            style={{ borderRadius: 2 }}
            data-testid="detail-close-btn"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Hero image */}
        <div className="relative h-56 bg-[var(--bg-emphasis)] border-b border-[var(--border-hover)] overflow-hidden">
          {form.image_url ? (
            <img src={resolveImageUrl(form.image_url)} alt={form.name} className="w-full h-full object-cover opacity-90" />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Car className="w-20 h-20 text-[var(--text-faint)]" strokeWidth={1} />
            </div>
          )}
          {/* Dark gradient + text — independent of theme since they sit over
              an arbitrary photo, not the page surface. */}
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
          <div className="absolute bottom-4 left-6 right-6 flex items-end justify-between">
            <div>
              <div className="text-[10px] tracking-[0.16em] uppercase text-white/70">{form.year || "—"} · {form.make || "Vehicle"}</div>
              <h2 className="font-display text-3xl font-light tracking-tighter text-white drop-shadow-md">
                {form.name}
              </h2>
            </div>
            {paint && (
              <div className="flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/15 px-3 py-1.5" style={{ borderRadius: 2 }}>
                <span className="w-3 h-3 border border-white/30" style={{ background: paint.hex, borderRadius: 1 }} />
                <span className="text-xs text-white">{paint.name}</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Yearly stats summary */}
          <div className="bg-[var(--bg-canvas)] border border-[var(--border-hover)] p-5" style={{ borderRadius: 2 }}>
            <div className="flex items-center justify-between mb-3">
              <span className="label-tiny">{stats?.year || new Date().getFullYear()} Mileage</span>
              <span
                className={`text-xs font-mono-data ${
                  overLimit ? "text-[#E31937]" : "text-[var(--text-primary)]"
                }`}
              >
                {pct.toFixed(1)}%
              </span>
            </div>
            <div className="h-1.5 bg-[var(--bg-hover)] mb-4 overflow-hidden">
              <div
                className="h-full transition-all"
                style={{
                  width: `${pct}%`,
                  background: overLimit ? "#E31937" : "var(--text-primary)",
                }}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="label-tiny mb-1">Used</div>
                <div className="font-mono-data text-lg text-[var(--text-primary)]">
                  {fmt(fromKm(usedKm, unit), 0)}
                  <span className="text-xs text-[var(--text-muted)] ml-1">{unitLabel(unit)}</span>
                </div>
              </div>
              <div>
                <div className="label-tiny mb-1">Limit</div>
                <div className="font-mono-data text-lg text-[var(--text-primary)]">
                  {fmt(fromKm(limit, unit), 0)}
                  <span className="text-xs text-[var(--text-muted)] ml-1">{unitLabel(unit)}</span>
                </div>
              </div>
              <div>
                <div className="label-tiny mb-1">Trips</div>
                <div className="font-mono-data text-lg text-[var(--text-primary)]">{stats?.trip_count || 0}</div>
              </div>
            </div>

            {monthly.length > 0 && (
              <div className="h-12 mt-4 -mx-1">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <Bar dataKey="value" fill={tc["bg-inverse"]} radius={[1, 1, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Average Efficiency (adaptive — EV/Hybrid → km/kWh; Gas → L/100km) */}
          <AvgEfficiencyBlock
            eff={stats?.efficiency}
            energyType={form.energy_type}
            unit={unit}
          />

          {/* Maintenance status card intentionally hidden here — the
              accordion "Maintenance" section below (Section list) is the
              canonical entry point. Import + fetch retained for easy
              re-enable. */}
          {false && (
            <MaintenanceStatusCard
              summary={maintSummary}
              vehicleName={vehicle.name}
              vehicleId={vehicle.id}
              unit={unit}
            />
          )}

          {/* -------- Energy History sub-view (4a) -------- */}
          {view === "energy-history" ? (
            <div data-testid="detail-energy-history-view">
              <button
                type="button"
                onClick={() => setView("main")}
                className="tap inline-flex items-center gap-1.5 text-xs tracking-wider uppercase text-[var(--text-muted)] hover:text-[var(--text-primary)] mb-3"
                data-testid="detail-energy-history-back"
              >
                <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
                Back to vehicle
              </button>
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-xl tracking-tight">Energy History</div>
                {onAddEnergy && (
                  <button
                    type="button"
                    onClick={() => onAddEnergy(vehicle.id)}
                    className="tap inline-flex items-center gap-1 text-[11px] tracking-wider uppercase text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                    data-testid="detail-add-energy-btn"
                  >
                    <Plus className="w-3 h-3" strokeWidth={2} />
                    Log Energy
                  </button>
                )}
              </div>
              {energyEvents.length === 0 ? (
                <div className="bg-[var(--bg-canvas)] border border-dashed border-[var(--border-hover)] p-4 text-xs text-[var(--text-muted)] text-center" style={{ borderRadius: 2 }}>
                  No energy entries yet. Log a {form.energy_type === "gas" ? "fuel fill-up" : form.energy_type === "hybrid" ? "charge or fuel entry" : "charging session"} to start tracking spend.
                </div>
              ) : (
                <div className="bg-[var(--bg-canvas)] border border-[var(--border-hover)]" style={{ borderRadius: 2 }}>
                  <div className="px-4 py-3 flex items-center justify-between border-b border-[var(--border-hover)]">
                    <div className="text-xs text-[var(--text-secondary)]">
                      {energyEvents.length} {energyEvents.length === 1 ? "entry" : "entries"}
                    </div>
                    <div className="font-mono-data text-sm text-[var(--text-primary)]">
                      {fmtMoney(totalSpend, currencySymbol, 0)}
                      <span className="text-[var(--text-muted)] text-xs ml-1">total</span>
                    </div>
                  </div>
                  <div className="max-h-[420px] overflow-auto">
                    {energyEvents.slice(0, 200).map((e) => {
                      const Icon = e.type === "fuel" ? Fuel : Zap;
                      return (
                        <div
                          key={e.id}
                          className="px-4 py-2.5 flex items-center gap-3 border-b border-[#161616] last:border-b-0 hover:bg-[var(--bg-input)] group"
                          data-testid={`energy-event-${e.id}`}
                        >
                          <div className="w-7 h-7 flex items-center justify-center bg-[var(--bg-emphasis)] border border-[var(--border-hover)]" style={{ borderRadius: 999 }}>
                            <Icon className="w-3.5 h-3.5 text-[var(--text-primary)]" strokeWidth={1.75} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-mono-data text-xs text-[var(--text-primary)]">{e.date}</div>
                            <div className="text-[11px] text-[var(--text-muted)] truncate">
                              {fmt(e.amount, 1)} {e.unit}
                              {e.notes ? ` · ${e.notes}` : ""}
                            </div>
                          </div>
                          <div className="font-mono-data text-sm text-[var(--text-primary)]">
                            {fmtMoney(e.cost, currencySymbol, 2)}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteEnergy(e.id)}
                            className="opacity-0 group-hover:opacity-100 text-[var(--text-muted)] hover:text-[#FF3B30] transition-colors"
                            data-testid={`energy-delete-${e.id}`}
                            aria-label="Delete energy entry"
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* -------- Accordion sections (main view) -------- */}
              <div className="space-y-2" data-testid="detail-sections">
                {/* ----- Basic Information ----- */}
                <SectionRow
                  id="basic"
                  icon={CarIcon}
                  title="Basic Information"
                  subtitle="Name, year, make, model, VIN, and photo"
                  open={openSection === "basic"}
                  onClick={() => setOpenSection(openSection === "basic" ? null : "basic")}
                  testid="section-basic"
                >
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="space-y-1">
                      <Label className="label-tiny">Name</Label>
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]" data-testid="detail-name" />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-tiny">Year</Label>
                      <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })}
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data" data-testid="detail-year" />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-tiny">Make</Label>
                      <Input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })}
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]" data-testid="detail-make" />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-tiny">Model</Label>
                      <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]" data-testid="detail-model" />
                    </div>
                    <div className="space-y-1 col-span-2">
                      <Label className="label-tiny">VIN</Label>
                      <Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                        placeholder="17-character VIN" maxLength={17}
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data tracking-wider" data-testid="detail-vin" />
                    </div>
                    <div className="space-y-1 col-span-2">
                      <Label className="label-tiny">Photo</Label>
                      <VehiclePhotoPicker value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} testIdPrefix="detail-photo" />
                    </div>
                  </div>
                </SectionRow>

                {/* ----- Specifications ----- */}
                <SectionRow
                  id="specs"
                  icon={ClipboardList}
                  title="Specifications"
                  subtitle="Wheel size, paint, EPA rating"
                  open={openSection === "specs"}
                  onClick={() => setOpenSection(openSection === "specs" ? null : "specs")}
                  testid="section-specs"
                >
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="space-y-1">
                      <Label className="label-tiny">Wheel Size</Label>
                      <Select value={form.wheel_size} onValueChange={(v) => setForm({ ...form, wheel_size: v })}>
                        <SelectTrigger className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data" data-testid="detail-wheel-size">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]">
                          {WHEEL_SIZES.map((w) => (<SelectItem key={w} value={w}>{w}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="label-tiny">Paint</Label>
                      <Select value={form.paint_name} onValueChange={(v) => setForm({ ...form, paint_name: v })}>
                        <SelectTrigger className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]" data-testid="detail-paint">
                          <SelectValue placeholder="Select color" />
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] max-h-[280px]">
                          {TESLA_COLORS.map((c) => (
                            <SelectItem key={c.code} value={c.name}>
                              <span className="flex items-center gap-2">
                                <span className="w-3 h-3 border border-[#333]" style={{ background: c.hex, borderRadius: 1 }} />
                                {c.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* EPA — adapts to energy_type. EV + Hybrid show kWh/100km; Gas + Hybrid show L/100km. */}
                    {(form.energy_type === "ev" || form.energy_type === "hybrid") && (
                      <div className="space-y-1 col-span-2 sm:col-span-1">
                        <Label className="label-tiny">EPA · kWh / 100 km</Label>
                        <Input type="number" step="0.1" min="0" value={form.epa_kwh_per_100km}
                          onChange={(e) => setForm({ ...form, epa_kwh_per_100km: e.target.value })}
                          placeholder="e.g. 18.5"
                          className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                          data-testid="detail-epa-kwh" />
                      </div>
                    )}
                    {(form.energy_type === "gas" || form.energy_type === "hybrid") && (
                      <div className="space-y-1 col-span-2 sm:col-span-1">
                        <Label className="label-tiny">EPA · L / 100 km</Label>
                        <Input type="number" step="0.1" min="0" value={form.epa_l_per_100km}
                          onChange={(e) => setForm({ ...form, epa_l_per_100km: e.target.value })}
                          placeholder="e.g. 7.4"
                          className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                          data-testid="detail-epa-l" />
                      </div>
                    )}
                  </div>
                </SectionRow>

                {/* ----- Energy Settings ----- */}
                <SectionRow
                  id="energy"
                  icon={Zap}
                  title="Energy Settings"
                  subtitle="Battery capacity, fuel tank (if applicable), energy type"
                  trailing={
                    <span className="text-[10px] tracking-wider uppercase px-2 py-0.5 border border-[var(--border-hover)] text-[var(--text-secondary)]" style={{ borderRadius: 2 }}>
                      {form.energy_type === "ev" ? "EV" : form.energy_type === "gas" ? "Gas" : "Hybrid"}
                    </span>
                  }
                  open={openSection === "energy"}
                  onClick={() => setOpenSection(openSection === "energy" ? null : "energy")}
                  testid="section-energy"
                >
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="space-y-1 col-span-2">
                      <Label className="label-tiny">Energy Type</Label>
                      <div className="inline-flex items-center bg-[var(--bg-canvas)] border border-[var(--border-hover)] p-0.5"
                        style={{ borderRadius: 2 }} data-testid="detail-energy-type">
                        {[["ev", "EV"], ["gas", "Gas"], ["hybrid", "Hybrid"]].map(([val, label]) => (
                          <button key={val} type="button"
                            onClick={() => setForm({ ...form, energy_type: val })}
                            data-testid={`detail-energy-${val}`}
                            className={`tap px-3 py-1 text-xs tracking-wider transition-colors ${
                              form.energy_type === val ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                            }`}
                            style={{ borderRadius: 1 }}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {(form.energy_type === "ev" || form.energy_type === "hybrid") && (
                      <div className="space-y-1 col-span-2 sm:col-span-1">
                        <Label className="label-tiny">Usable Battery (kWh)</Label>
                        <Input type="number" step="0.1" min="0" value={form.usable_battery_kwh}
                          onChange={(e) => setForm({ ...form, usable_battery_kwh: e.target.value })}
                          placeholder="e.g. 75"
                          className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                          data-testid="detail-battery-kwh" />
                      </div>
                    )}
                    {(form.energy_type === "gas" || form.energy_type === "hybrid") && (
                      <div className="space-y-1 col-span-2 sm:col-span-1">
                        <Label className="label-tiny">Fuel Tank (L) · optional</Label>
                        <Input type="number" step="0.1" min="0" value={form.fuel_tank_capacity_l}
                          onChange={(e) => setForm({ ...form, fuel_tank_capacity_l: e.target.value })}
                          placeholder="—"
                          className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                          data-testid="detail-fuel-tank" />
                      </div>
                    )}
                  </div>
                </SectionRow>

                {/* ----- Usage & Limits ----- */}
                <SectionRow
                  id="usage"
                  icon={Gauge}
                  title="Usage & Limits"
                  subtitle="Yearly limit, odometer, purchase date, usage type"
                  open={openSection === "usage"}
                  onClick={() => setOpenSection(openSection === "usage" ? null : "usage")}
                  testid="section-usage"
                >
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    {/* Read-only, authoritative Current Odometer. Resolved
                        via the shared OdometerService — reflects the
                        highest of manual baseline, latest trip, latest
                        energy event, or (future) OEM telemetry. Never
                        derived here. */}
                    <div
                      className="col-span-2 flex items-center justify-between bg-[var(--bg-canvas)] border border-[var(--border-hover)] px-3 py-2"
                      style={{ borderRadius: 2 }}
                      data-testid="detail-current-odometer-tile"
                    >
                      <div>
                        <div className="label-tiny">Current Odometer</div>
                        {sheetOdo.source && sheetOdo.source !== "none" && (
                          <div className="text-[10px] tracking-wider uppercase text-[var(--text-muted)] font-mono-data mt-0.5">
                            {odometerSourceLabel(sheetOdo.source)}
                          </div>
                        )}
                      </div>
                      <div
                        className="font-mono-data text-base text-[var(--text-primary)]"
                        data-testid="detail-current-odometer-value"
                      >
                        {sheetOdo.km == null || sheetOdo.km <= 0
                          ? "—"
                          : `${fromKm(sheetOdo.km, unit).toFixed(0)} ${unitLabel(unit)}`}
                      </div>
                    </div>
                    <div className="space-y-1 col-span-2">
                      <Label className="label-tiny">Yearly Limit ({unitLabel(unit)})</Label>
                      <Input type="number" value={form.yearly_limit_display}
                        onChange={(e) => setForm({ ...form, yearly_limit_display: e.target.value })}
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                        data-testid="detail-limit" />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-tiny flex items-center gap-1.5">
                        <span>Starting Odometer ({unitLabel(unit)})</span>
                        <span
                          className="text-[9px] tracking-wider uppercase text-[var(--text-muted)]"
                          title="One-time baseline: the odometer reading at the point you started tracking this vehicle. The Current Odometer above updates automatically from your trips, charging sessions, and (in the future) OEM telemetry."
                        >
                          Baseline
                        </span>
                      </Label>
                      <Input type="number" min="0" step="1" value={form.current_odometer_km}
                        onChange={(e) => setForm({ ...form, current_odometer_km: e.target.value })}
                        placeholder="—"
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                        data-testid="detail-starting-odometer" />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-tiny">Purchase Date</Label>
                      <Input type="date" value={form.purchase_date}
                        onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                        data-testid="detail-purchase-date" />
                    </div>
                    <div className="space-y-1 col-span-2">
                      <Label className="label-tiny">Primary Usage Type</Label>
                      <div className="inline-flex items-center bg-[var(--bg-canvas)] border border-[var(--border-hover)] p-0.5"
                        style={{ borderRadius: 2 }} data-testid="detail-primary-usage-type">
                        {[["personal", "Personal"], ["business", "Business"], ["mixed", "Mixed"]].map(([val, label]) => (
                          <button key={val} type="button"
                            onClick={() => setForm({ ...form, primary_usage_type: val })}
                            data-testid={`detail-usage-${val}`}
                            className={`tap px-3 py-1 text-xs tracking-wider transition-colors ${
                              form.primary_usage_type === val ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                            }`}
                            style={{ borderRadius: 1 }}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </SectionRow>

                {/* ----- Energy History (sub-view trigger) ----- */}
                <button
                  type="button"
                  onClick={() => setView("energy-history")}
                  className="tap w-full flex items-center gap-3 px-4 py-3 bg-[var(--bg-canvas)] border border-[var(--border-hover)] hover:bg-[var(--bg-hover)] text-left transition-colors"
                  style={{ borderRadius: 2 }}
                  data-testid="section-energy-history"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-[var(--bg-emphasis)] border border-[var(--border-hover)]" style={{ borderRadius: 999 }}>
                    <ChartLine className="w-4 h-4 text-[var(--text-primary)]" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--text-primary)]">Energy History</div>
                    <div className="text-xs text-[var(--text-muted)] truncate">
                      {energyEvents.length} {energyEvents.length === 1 ? "entry" : "entries"} · {fmtMoney(totalSpend, currencySymbol, 0)} total
                    </div>
                  </div>
                  {onAddEnergy && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onAddEnergy(vehicle.id); }}
                      className="tap inline-flex items-center gap-1 text-[11px] tracking-wider uppercase text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                      data-testid="detail-row-log-energy"
                    >
                      <Plus className="w-3 h-3" strokeWidth={2} />
                      Log Energy
                    </button>
                  )}
                  <ChevronRight className="w-4 h-4 text-[var(--text-faint)] shrink-0" strokeWidth={1.5} />
                </button>

                {/* ----- Maintenance ----- */}
                <SectionRow
                  id="maintenance"
                  icon={Wrench}
                  //iconTone="#E82127" //Adding a red tone to the wrench icon
                  title="Maintenance"
                  subtitle="Service schedule, reminders, and history"
                  open={openSection === "maintenance"}
                  onClick={() => setOpenSection(openSection === "maintenance" ? null : "maintenance")}
                  testid="section-maintenance"
                >
                  <MaintenanceAccordionContent
                    items={maintItems}
                    summary={maintSummary}
                    unit={unit}
                    onGoToMaintenance={() =>
                      navigate(`/maintenance?vehicle_id=${vehicle.id}`)
                    }
                  />
                </SectionRow>

                {/* ----- Insurance ----- */}
                <SectionRow
                  id="insurance"
                  icon={Shield}
                  title="Insurance"
                  subtitle="Policy details and insurance information"
                  open={openSection === "insurance"}
                  onClick={() => setOpenSection(openSection === "insurance" ? null : "insurance")}
                  testid="section-insurance"
                >
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="space-y-1 col-span-2">
                      <Label className="label-tiny">Insurance Company</Label>
                      <Input value={form.insurance_company}
                        onChange={(e) => setForm({ ...form, insurance_company: e.target.value })}
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
                        data-testid="detail-ins-company" />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-tiny">Policy Number</Label>
                      <Input value={form.policy_number}
                        onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                        data-testid="detail-policy-number" />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-tiny">Policy Start</Label>
                      <Input type="date" value={form.policy_start_date}
                        onChange={(e) => setForm({ ...form, policy_start_date: e.target.value })}
                        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                        data-testid="detail-policy-start" />
                    </div>
                  </div>
                </SectionRow>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t border-[var(--border-hover)]">
                <Button onClick={save}
                  className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)] flex-1"
                  style={{ borderRadius: 2 }} data-testid="detail-save-btn">
                  <Save className="w-4 h-4 mr-2" strokeWidth={1.75} />
                  Save
                </Button>
                <Button variant="outline" onClick={() => onArchive(vehicle.id, !vehicle.archived)}
                  className="bg-transparent border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-emphasis)]"
                  style={{ borderRadius: 2 }} data-testid="detail-archive-btn">
                  {vehicle.archived ? <ArchiveRestore className="w-4 h-4" strokeWidth={1.5} /> : <Archive className="w-4 h-4" strokeWidth={1.5} />}
                </Button>
                <Button variant="outline" onClick={() => onDelete(vehicle.id)}
                  className="bg-transparent border-[var(--border-hover)] text-[#E31937] hover:bg-[#E31937]/10 hover:text-[#E31937]"
                  style={{ borderRadius: 2 }} data-testid="detail-delete-btn">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
      <ConfirmDialog />
    </Sheet>
  );
}

/* ---------------- Accordion section primitive ----------------
 * A clickable row (icon · title · subtitle · chevron) that toggles its
 * `children` into view directly below. Used by the refactored Vehicle
 * Detail sheet so users see the full menu at a glance without scrolling
 * through every field. `iconTone` optionally coloUrs the leading icon
 * (used to give Maintenance its red accent). */
function SectionRow({ icon: Icon, iconTone, title, subtitle, trailing, open, onClick, testid, children }) {
  return (
    <div
      className="bg-[var(--bg-canvas)] border border-[var(--border-hover)]"
      style={{ borderRadius: 2 }}
      data-testid={testid}
      data-open={open ? "true" : "false"}
    >
      <button
        type="button"
        onClick={onClick}
        className="tap w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--bg-hover)]"
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
      >
        <div className="w-8 h-8 flex items-center justify-center bg-[var(--bg-emphasis)] border border-[var(--border-hover)]" style={{ borderRadius: 999 }}>
          <Icon className="w-4 h-4" strokeWidth={1.75} style={{ color: iconTone || "var(--text-primary)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[var(--text-primary)]">{title}</div>
          {subtitle && <div className="text-xs text-[var(--text-muted)] truncate">{subtitle}</div>}
        </div>
        {trailing}
        <ChevronRight
          className={`w-4 h-4 text-[var(--text-faint)] shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
          strokeWidth={1.5}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[var(--border-hover)]" data-testid={`${testid}-content`}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ---------------- Maintenance accordion content ----------------
 * Mobile-first "At a glance" 3-pill layout + "Next due" callout +
 * "Go to Maintenance" CTA. Matches the reference design shown by the user.
 *
 * The three pills bucket items into overdue / due-soon / healthy — for each
 * bucket we surface the count AND the single most-urgent item's name to make
 * the info scannable without opening the full page. */
function MaintenanceAccordionContent({ items, summary, unit, onGoToMaintenance }) {
  const buckets = { overdue: [], due_soon: [], healthy: [] };
  for (const it of items || []) {
    if (buckets[it.status]) buckets[it.status].push(it);
  }
  const counts = summary?.counts || { overdue: 0, due_soon: 0, healthy: 0, unknown: 0 };
  const nextUp = summary?.next_upcoming || null;

  const pills = [
    {
      key: "overdue",
      count: counts.overdue,
      label: counts.overdue === 1 ? "Overdue" : "Overdue",
      color: "#FF3B30",
      top: buckets.overdue[0],
    },
    {
      key: "due_soon",
      count: counts.due_soon,
      label: counts.due_soon === 1 ? "Due Soon" : "Due Soon",
      color: "#FFB340",
      top: buckets.due_soon[0],
    },
    {
      key: "healthy",
      count: counts.healthy,
      label: counts.healthy === 1 ? "Upcoming" : "Upcoming",
      color: "#34D17A",
      top: buckets.healthy[0],
    },
  ];

  const nextMeta = nextUp ? statusMeta(nextUp.status) : null;
  const NextIcon = nextUp ? categoryIcon(nextUp.category) : Wrench;

  return (
    <div className="pt-3 space-y-4">
      {/* AT A GLANCE — 3 pill layout mirroring the mobile mock */}
      <div>
        <div className="label-tiny mb-2">At a glance</div>
        <div className="grid grid-cols-3 gap-2">
          {pills.map((p) => {
            const TopIcon = p.top ? categoryIcon(p.top.category) : Wrench;
            return (
              <div
                key={p.key}
                className="bg-[var(--bg-elevated)] border border-[var(--border-hover)] px-2.5 py-3 flex flex-col items-start gap-1.5"
                style={{ borderRadius: 2 }}
                data-testid={`maint-pill-${p.key}`}
              >
                <div
                  className="w-7 h-7 shrink-0 flex items-center justify-center"
                  style={{
                    background: `${p.color}22`,
                    borderRadius: 999,
                  }}
                >
                  <TopIcon className="w-3.5 h-3.5" strokeWidth={1.75} style={{ color: p.color }} />
                </div>
                <div className="min-w-0 w-full">
                  <div className="font-mono-data text-sm text-[var(--text-primary)] leading-none">
                    {p.count} <span className="text-[10px] tracking-wider uppercase text-[var(--text-muted)] font-sans">{p.label}</span>
                  </div>
                  {/* --- Per-pill detail intentionally hidden (see NEXT DUE
                        block below for the single most-urgent item). Kept
                        as comments so re-enabling is a one-block uncomment.
                  {p.top ? (
                    <>
                      <div className="text-[11px] text-[var(--text-primary)] mt-1 truncate">
                        {p.top.name}
                      </div>
                      <div className="text-[10px] font-mono-data text-[var(--text-muted)] truncate">
                        {formatRemaining(p.top, unit) || "—"}
                      </div>
                    </>
                  ) : (
                    <div className="text-[10px] text-[var(--text-faint)] mt-1">—</div>
                  )}
                  --- */}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* NEXT DUE — single most-urgent item across all buckets */}
      {nextUp && (
        <div>
          <div className="label-tiny mb-2">Next due</div>
          <button
            type="button"
            onClick={onGoToMaintenance}
            className="tap w-full flex items-center gap-3 px-3 py-3 bg-[var(--bg-elevated)] border border-[var(--border-hover)] hover:bg-[var(--bg-hover)] text-left transition-colors"
            style={{ borderRadius: 2, borderLeft: `2px solid ${nextMeta?.color || "#8A8A8E"}` }}
            data-testid="maint-next-due"
          >
            <div
              className="w-9 h-9 shrink-0 flex items-center justify-center"
              style={{ background: `${nextMeta?.color || "#8A8A8E"}18`, borderRadius: 999 }}
            >
              <NextIcon className="w-4 h-4" strokeWidth={1.75} style={{ color: nextMeta?.color || "var(--text-primary)" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[var(--text-primary)] truncate">
                {nextUp.name}
              </div>
              <div className="text-xs text-[var(--text-muted)] font-mono-data truncate">
                {formatRemaining(nextUp, unit) || "—"}
              </div>
            </div>
            {nextUp.due_at_date && (
              <div className="text-right shrink-0">
                <div className="label-tiny">Due</div>
                <div className="text-xs font-mono-data text-[var(--text-primary)]">
                  {formatDueDate(nextUp.due_at_date)}
                </div>
              </div>
            )}
            <ChevronRight className="w-4 h-4 text-[var(--text-faint)] shrink-0" strokeWidth={1.5} />
          </button>
        </div>
      )}

      {/* CTA */}
      <button
        type="button"
        onClick={onGoToMaintenance}
        className="tap w-full inline-flex items-center justify-center gap-2 h-11 bg-[#E82127] hover:bg-[#FF2A30] text-white text-sm tracking-wider uppercase transition-colors"
        style={{ borderRadius: 2 }}
        data-testid="maint-go-btn"
      >
        <CalendarIcon className="w-4 h-4" strokeWidth={2} />
        Go to Maintenance
      </button>

      {items.length === 0 && (
        <div className="text-center text-xs text-[var(--text-muted)] py-2">
          No maintenance items yet — open the Maintenance page to seed defaults.
        </div>
      )}
    </div>
  );
}

// Format an ISO date like "Jun 5, 2026" for the "Due" callout.
function formatDueDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch (_) { return iso; }
}

